import type { FC } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'react-native-paper';

import { HandFilledIcon, HandIcon } from '@/assets';
import { useAnnounceHappeningParticipation } from '@/features/api/hooks/grapevine-happening-events/useAnnounceHappeningParticipation';
import { useRevokeHappeningParticipation } from '@/features/api/hooks/grapevine-happening-events/useRevokeHappeningParticipation';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { isEmptyOrNull } from '@/shared/utils/string';
import { outlinedButtonLabelStyle } from '@/styles/customStyles';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

type ParticipateButtonProps = {
  postId?: string;
  participated?: boolean;
};

// Toggles between contained (participating) and outlined (not participating),
// so it cannot use OutlinedButton — the outline theme is applied inline for
// the outlined state (unused in contained mode).
export const ParticipateButton: FC<ParticipateButtonProps> = ({
  postId = '',
  participated = false
}) => {
  const { t } = useTranslation(LocalizationNameSpace.POSTS, {
    keyPrefix: 'screens.post-detail'
  });
  const theme = useAppTheme();
  const announce = useAnnounceHappeningParticipation();
  const revoke = useRevokeHappeningParticipation();

  const handleParticipateToggle = useCallback(() => {
    if (isEmptyOrNull(postId)) return;

    if (participated) {
      revoke.mutate(postId);
    } else {
      announce.mutate(postId);
    }
  }, [postId, participated, revoke, announce]);

  return (
    <Button
      disabled={announce.isPending || revoke.isPending}
      mode={participated ? 'contained' : 'outlined'}
      labelStyle={[participated ? {} : outlinedButtonLabelStyle]}
      onPress={handleParticipateToggle}
      icon={({ color, size }) =>
        participated ? (
          <HandFilledIcon fill={color} width={size} height={size} />
        ) : (
          <HandIcon fill={color} width={size} height={size} />
        )
      }
      theme={{ colors: { outline: theme.colors.primary } }}
    >
      {t('announceInterest')}
    </Button>
  );
};
